import * as React from 'react';
import { Node } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import {
  isNodeEmpty,
  Placeholder,
  IMAGE_DEFINITIONS,
  IMAGE_CAPTION_DEFINITIONS,
} from '@xing-com/content-editor-plugin-helpers';
import {
  scale060,
  scale100,
  scale150,
  xdlColorTextTertiary,
} from '@xing-com/tokens';

import { ImageToolbar } from './ImageToolbar';
import { removeImage } from './utils';

type ImageComponentProps = {
  children: React.ReactNode;
  className?: string;
  src: string;
  node: Node;
};

type ImageCaptionProps = {
  children: React.ReactNode;
  className?: string;
};
export const StyledImage = styled.img`
  display: block;
  margin: 0 auto;
  max-width: 100%;
  padding: ${scale150} 0 ${scale060};
`;

export const ImageWrapper = styled.div`
  position: relative;
`;

export const StyledCaption = styled.figcaption`
  color: ${xdlColorTextTertiary};
  padding: ${scale060} ${scale100} ${scale150};
  display: flex;
  justify-content: center;
  text-align: start;
`;

const ImageComponent = ({
  children,
  className,
  node,
  src,
  ...attributes
}: ImageComponentProps) => {
  const editor = useSlateStatic() as ReactEditor;
  return (
    <div {...attributes}>
      <ImageWrapper contentEditable={false}>
        <ImageToolbar
          onDelete={() => {
            removeImage(editor, node);
          }}
        />
        <StyledImage className={className} src={src} />
      </ImageWrapper>
      {children}
    </div>
  );
};

const CaptionComponent = ({
  children,
  className,
  ...attributes
}: ImageCaptionProps) => (
  <StyledCaption {...attributes}>{children}</StyledCaption>
);

// @ts-expect-error Leftover after Brewery migration
const Image = ({ className, ...attributes }) => {
  // @ts-expect-error Leftover after Brewery migration
  const RenderLeaf = ({ element, children }) => {
    const { type, data } = element;
    const { placeholder, ...restOfAttributes } = attributes;
    if (type === IMAGE_DEFINITIONS.type) {
      const { url } = data;
      return (
        <ImageComponent
          className={className}
          node={element}
          src={url}
          {...restOfAttributes}
        >
          {children}
        </ImageComponent>
      );
    }
    if (type === IMAGE_CAPTION_DEFINITIONS.type) {
      return (
        <CaptionComponent className={className} {...restOfAttributes}>
          {isNodeEmpty(element) && placeholder
            ? Placeholder(children, null, placeholder)
            : children}
        </CaptionComponent>
      );
    }
    return null;
  };
  return RenderLeaf;
};

export { Image };
